import React, { useState } from 'react';
import { Button, Checkbox, Modal, Field, FieldLabel, FieldError } from '../ui';
import { Lesson } from '@court-sync/shared';

export type CancelEntireLessonVariant = 'term' | 'package' | 'camp';

interface CancelTermLessonModalProps {
  isOpen: boolean;
  lesson: Lesson | null;
  academyId: string;
  isLoading?: boolean;
  stackLevel?: 'default' | 'elevated';
  /** term = group/term-bound private; package = standalone private */
  variant?: CancelEntireLessonVariant;
  makeupLessonEnabled?: boolean;
  onCancel: () => void;
  onConfirm: (
    reason: string,
    notifyStudents: boolean,
    grantMakeupCredit: boolean
  ) => Promise<void>;
}

export const CancelTermLessonModal: React.FC<CancelTermLessonModalProps> = ({
  isOpen,
  lesson,
  academyId,
  isLoading = false,
  stackLevel = 'default',
  variant = 'term',
  makeupLessonEnabled = false,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [notifyStudents, setNotifyStudents] = useState(true);
  const [grantMakeupCredit, setGrantMakeupCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isPackage = variant === 'package';
  const isCamp = variant === 'camp';
  const title = isCamp
    ? 'Cancel entire camp'
    : isPackage
      ? 'Cancel entire package'
      : 'Cancel entire term lesson';
  const warningBody = isCamp ? (
    <>
      This will cancel <strong>all remaining camp sessions</strong> and cancel associated bookings.
      Enrolled students will be notified and can rebook or request a refund where applicable.
    </>
  ) : isPackage ? (
    <>
      This will cancel <strong>all remaining sessions</strong> in this private lesson package and cancel
      associated bookings. Students can rebook or request a refund where applicable.
    </>
  ) : (
    <>
      This will cancel the lesson for <strong>all remaining sessions</strong> this term. Students will be
      notified and can rebook or request a refund for paid bookings.
    </>
  );
  const submitLabel = isCamp ? 'Cancel camp' : isPackage ? 'Cancel package' : 'Cancel entire term';

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await onConfirm(reason, notifyStudents, grantMakeupCredit);
      setReason('');
      setNotifyStudents(true);
      setGrantMakeupCredit(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel lesson');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setNotifyStudents(true);
    setGrantMakeupCredit(false);
    setError('');
    onCancel();
  };

  if (!isOpen || !lesson || !academyId) {
    return null;
  }

  const supported =
    lesson.lessonType === 'camp' ||
    (lesson.lessonType === 'group' && !!lesson.termId) ||
    (lesson.lessonType === 'private' && (isPackage ? !lesson.termId : !!lesson.termId));
  if (!supported) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} maxWidth="sm" stackLevel={stackLevel}>
      <div>
        <div className="space-y-4 mb-6">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-amber-900 dark:text-amber-100 text-sm">
            <strong>Warning:</strong> {warningBody}
          </div>

          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-gray-200">Lesson:</strong> {lesson.name}
            </p>
          </div>

          <Field>
            <FieldLabel>Cancellation reason</FieldLabel>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError('');
              }}
              placeholder="E.g., Academy discontinuing lesson, Coach unavailable"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              disabled={submitting || isLoading}
            />
            {error && <FieldError>{error}</FieldError>}
          </Field>

          <div className="flex items-start gap-3">
            <Checkbox
              id="notify-students-term"
              checked={notifyStudents}
              onCheckedChange={(checked) =>
                setNotifyStudents(typeof checked === 'boolean' ? checked : false)
              }
              disabled={submitting || isLoading}
            />
            <label
              htmlFor="notify-students-term"
              className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
            >
              Notify students/guardians and send rebook link
            </label>
          </div>

          {makeupLessonEnabled && (
            <div className="flex items-start gap-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
              <Checkbox
                id="grant-makeup-term-cancel"
                checked={grantMakeupCredit}
                onCheckedChange={(checked) =>
                  setGrantMakeupCredit(typeof checked === 'boolean' ? checked : false)
                }
                disabled={submitting || isLoading}
              />
              <label
                htmlFor="grant-makeup-term-cancel"
                className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer leading-snug"
              >
                {isPackage
                  ? 'Grant a make-up credit for the full package to enrolled students'
                  : isCamp
                    ? 'Grant a make-up session credit to enrolled students'
                    : 'Grant a make-up term credit to enrolled students'}
              </label>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={handleClose} disabled={submitting || isLoading}>
            Close
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={submitting || isLoading}
            isLoading={submitting || isLoading}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelTermLessonModal;
